import { useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { observer } from 'mobx-react';
import {
  getDefaultValuesForForm,
  IconLibrary,
  PermissionButton,
} from '@product-framework/index';
import schema from './create.json';
import validationSchema from './validations';

const FORM_ID = 'CreateServiceProviderNewLocationForm';

function ServiceProviderLocationCreate({ serviceProviderId: pageId }) {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, getApiUrl } = useDataProvider(provider);

  const getEditUrl = newId => {
    return `/serviceproviders/edit/${pageId}/locations/${newId}`;
  };

  const createSPLocation = async data => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers/${pageId}/locations`;
    let address = {};
    if (data?.businessAddress) {
      address = {
        address: {
          addressType: 'OFFICE',
          placeId: data?.businessAddress?.id,
          placeSessionToken: data?.businessAddressSessionToken,
        },
      };
    }

    const requestData = {
      locationName: data.locationName,
      locationNumber: data.locationNumber,
      isDefaultLocation: data.isDefaultLocation === true,
      serviceTravelDistance: data.serviceTravelDistance,
      status: data.status,
      contact: {
        userId: data?.mainContact?.user?.id,
        contactType: 'MAIN',
        isPrimary: true,
      },
      ...address,
    };
    const response = await post(url, requestData);
    return response;
  };

  const onSubmit = async data => {
    const response = await createSPLocation(data);
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICE_PROVIDER_LOCATION_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'Service Provider location was saved successfully',
        }),
      );
      return {
        nextValues: data,
        errors: null,
        callback: () => navigate(getEditUrl(response?.data?.id)),
      };
    }

    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const defaultValues = useMemo(
    () => ({
      ...getDefaultValuesForForm({
        schema,
      }),
      serviceProviderId: pageId,
      isDefaultLocation: false,
      serviceTravelDistance: 0.0,
    }),
    [pageId],
  );

  return (
    <Box>
      <Form
        formId={FORM_ID}
        ref={formRef}
        permittedByDefault
        onSubmit={onSubmit}
        schema={schema}
        defaultValues={defaultValues}
        validationSchema={validationSchema(formatMessage)}>
        <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
          <PermissionButton
            permissionName="CAN_CREATE_SP_LOCATIONS"
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            {formatMessage({
              id: 'SAVE_LOCATION',
              defaultMessage: 'Save Location',
            })}
          </PermissionButton>
          <PermissionButton
            permissionName="CAN_CREATE_SP_LOCATIONS"
            variant="contained"
            color="primary"
            startIcon={<IconLibrary.Reset />}
            onClick={() => {
              formRef.current?.reset();
            }}>
            {formatMessage({
              id: 'RESET',
              defaultMessage: 'Reset',
            })}
          </PermissionButton>
        </Box>
      </Form>
    </Box>
  );
}

export default observer(ServiceProviderLocationCreate);
